import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Search, UserPlus, ChevronDown, Loader2, Users } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { projectService } from '../api/projectsApi';
import { userService } from '../../users';
import { BRAND_COLORS, SYSTEM_COLORS, NEUTRAL_COLORS } from '../../../constants/brandPalette';

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 'planning',   label: 'Planning'   },
  { value: 'active',     label: 'Active'     },
  { value: 'inProgress', label: 'In Progress'},
  { value: 'on_hold',    label: 'On Hold'    },
  { value: 'completed',  label: 'Completed'  },
  { value: 'cancelled',  label: 'Cancelled'  },
];

const TEAM_ROLE_OPTIONS = [
  'developer', 'designer', 'tester', 'leader', 'manager',
  'analyst', 'marketing', 'pr', 'graphics', 'other',
];

const AVATAR_PALETTE = ['#37559e','#bf6f50','#15803d','#b45309','#1d4ed8','#7c3aed','#0891b2','#be123c'];
const getInitials = (name) =>
  name ? name.trim().split(/\s+/).map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?';
const getAvatarColor = (name) =>
  AVATAR_PALETTE[(name?.charCodeAt(0) ?? 0) % AVATAR_PALETTE.length];

// ─── Helper: normalize a user record from the users API ──────────────────────
const normalizeUser = (u) => ({
  id:     u.id    || u._id   || u.user_id || '',
  name:   u.name  || u.fullName || u.full_name || u.username || u.email || 'Unknown',
  email:  u.email || '',
  role:   u.role  || '',
  skills: Array.isArray(u.skills) ? u.skills : [],
});

// ─── Helper: normalize users list response ───────────────────────────────────
const normalizeUsersList = (payload) => {
  const list = Array.isArray(payload) ? payload
    : Array.isArray(payload?.users) ? payload.users
    : Array.isArray(payload?.data)  ? payload.data
    : [];
  return list.map(normalizeUser);
};

// ─── TeamMemberPicker ─────────────────────────────────────────────────────────
const TeamMemberPicker = ({ value, onChange }) => {
  const [allUsers, setAllUsers]       = useState([]);
  const [query, setQuery]             = useState('');
  const [dropOpen, setDropOpen]       = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const searchRef = useRef(null);

  // load users once
  useEffect(() => {
    setLoadingUsers(true);
    userService.getAllUsers()
      .then(raw => setAllUsers(normalizeUsersList(raw)))
      .catch(() => {
        // fallback to active users
        return userService.getActiveUsers()
          .then(raw => setAllUsers(normalizeUsersList(raw)));
      })
      .finally(() => setLoadingUsers(false));
  }, []);

  const memberIds = new Set(value.map(m => m.id));

  const filtered = allUsers
    .filter(u => !memberIds.has(u.id))
    .filter(u => {
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    })
    .slice(0, 12);

  const addMember = (user) => {
    onChange([...value, { ...user, role: user.role || 'developer' }]);
    setQuery('');
    setDropOpen(false);
  };

  const removeMember = (id) => onChange(value.filter(m => m.id !== id));

  const updateRole = (id, role) => {
    onChange(value.map(m => m.id === id ? { ...m, role } : m));
    setEditingRoleId(null);
  };

  // close dropdown on outside click
  useEffect(() => {
    const close = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="space-y-3">
      {/* Search box */}
      <div ref={searchRef} className="relative">
        <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg focus-within:border-[#37559e] focus-within:ring-2 focus-within:ring-[#37559e]/20 bg-white transition">
          <Search size={15} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setDropOpen(true); }}
            onFocus={() => setDropOpen(true)}
            placeholder="Search by name or email…"
            className="flex-1 text-sm outline-none bg-transparent placeholder-gray-400"
          />
          {loadingUsers && <Loader2 size={14} className="animate-spin text-gray-400 flex-shrink-0" />}
        </div>

        {/* Dropdown */}
        {dropOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-400 text-center">
                {loadingUsers ? 'Loading users…' : 'No users found'}
              </p>
            ) : (
              filtered.map(u => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => addMember(u)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50 transition-colors text-left"
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                    style={{ backgroundColor: getAvatarColor(u.name) }}
                  >
                    {getInitials(u.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                    <p className="text-xs text-gray-500 truncate">{u.email}</p>
                  </div>
                  <UserPlus size={14} className="text-[#37559e] flex-shrink-0" />
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Selected members */}
      {value.length > 0 ? (
        <div className="space-y-2">
          {value.map(m => (
            <div
              key={m.id}
              className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg"
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                style={{ backgroundColor: getAvatarColor(m.name) }}
              >
                {getInitials(m.name)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">{m.name}</p>
                <p className="text-xs text-gray-500 truncate">{m.email}</p>
              </div>

              {/* Role selector */}
              <div className="relative flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setEditingRoleId(editingRoleId === m.id ? null : m.id)}
                  className="flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs font-medium text-gray-700 hover:border-[#37559e] hover:text-[#37559e] transition-colors"
                >
                  {m.role || 'role'}
                  <ChevronDown size={11} />
                </button>
                {editingRoleId === m.id && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 min-w-[120px]">
                    {TEAM_ROLE_OPTIONS.map(r => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => updateRole(m.id, r)}
                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 transition-colors ${
                          m.role === r ? 'text-[#37559e] font-semibold' : 'text-gray-700'
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => removeMember(m.id)}
                className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                title="Remove"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1 py-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <Users size={20} className="text-gray-300" />
          <p className="text-xs text-gray-400">No team members yet — search above to add</p>
        </div>
      )}
    </div>
  );
};

// ─── ProjectEditModal ─────────────────────────────────────────────────────────
const ProjectEditModal = ({ project, isOpen, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Populate form from project whenever it changes or modal opens
  useEffect(() => {
    if (!project || !isOpen) return;
    setForm({
      name:           project.name           || '',
      description:    project.description    || '',
      status:         project.status         || 'planning',
      startDate:      (project.start_date    || project.startDate    || '').split('T')[0],
      endDate:        (project.end_date      || project.endDate      || '').split('T')[0],
      progress:       project.progress       ?? 0,
      projectManager: project.project_manager_id || project.project_manager || '',
      team: (project.members || project.team || []).map(m => ({
        id:     m.id    || m.user_id || '',
        name:   m.name  || '',
        email:  m.email || '',
        role:   m.role  || '',
        skills: Array.isArray(m.skills) ? m.skills : [],
      })),
    });
  }, [project, isOpen]);

  const set = useCallback((key, val) => setForm(f => ({ ...f, [key]: val })), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error('Project name is required'); return; }

    const payload = {
      name:            form.name.trim(),
      description:     form.description.trim(),
      status:          form.status,
      progress:        Number(form.progress),
      ...(form.startDate      && { start_date:      form.startDate }),
      ...(form.endDate        && { end_date:        form.endDate }),
      ...(form.projectManager && { project_manager: form.projectManager }),
      team: form.team.map(m => ({
        id:     m.id,
        name:   m.name,
        role:   m.role   || '',
        email:  m.email  || '',
        skills: m.skills || [],
      })),
    };

    setSaving(true);
    try {
      const updated = await projectService.updateProject(project.id, payload);
      toast.success('Project updated');
      onSaved?.(updated);
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !project) return null;

  const inputCls =
    'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white ' +
    'focus:outline-none focus:border-[#37559e] focus:ring-2 focus:ring-[#37559e]/20 transition';

  const labelCls = 'block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide';

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div
        className="relative w-full sm:max-w-2xl flex flex-col bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl"
        style={{ maxHeight: '92dvh' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0"
          style={{ borderColor: SYSTEM_COLORS.borderSoft }}
        >
          <div>
            <h2 className="text-base font-bold" style={{ color: NEUTRAL_COLORS.black }}>Edit Project</h2>
            <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>{project.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <form id="project-edit-form" onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-5 py-5 space-y-5">

          {/* Name */}
          <div>
            <label className={labelCls}>Project Name *</label>
            <input
              type="text"
              value={form.name || ''}
              onChange={e => set('name', e.target.value)}
              className={inputCls}
              placeholder="Project name"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className={labelCls}>Description</label>
            <textarea
              value={form.description || ''}
              onChange={e => set('description', e.target.value)}
              className={`${inputCls} resize-none`}
              rows={3}
              placeholder="What is this project about?"
            />
          </div>

          {/* Status + Progress row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Status</label>
              <select
                value={form.status || 'planning'}
                onChange={e => set('status', e.target.value)}
                className={inputCls}
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Progress — {form.progress ?? 0}%</label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={form.progress ?? 0}
                  onChange={e => set('progress', Number(e.target.value))}
                  className="flex-1 accent-[#37559e]"
                />
                <span className="text-xs font-semibold text-[#37559e] w-8 text-right">
                  {form.progress ?? 0}%
                </span>
              </div>
              {/* Progress bar preview */}
              <div className="mt-1.5 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#37559e] rounded-full transition-all duration-150"
                  style={{ width: `${form.progress ?? 0}%` }}
                />
              </div>
            </div>
          </div>

          {/* Date row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Start Date</label>
              <input
                type="date"
                value={form.startDate || ''}
                onChange={e => set('startDate', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>End Date</label>
              <input
                type="date"
                value={form.endDate || ''}
                onChange={e => set('endDate', e.target.value)}
                min={form.startDate || undefined}
                className={inputCls}
              />
            </div>
          </div>

          {/* Divider */}
          <hr style={{ borderColor: SYSTEM_COLORS.borderSoft }} />

          {/* Team */}
          <div>
            <label className={labelCls}>
              <span className="flex items-center gap-1.5">
                <Users size={12} />
                Team Members ({(form.team || []).length})
              </span>
            </label>
            <TeamMemberPicker
              value={form.team || []}
              onChange={team => set('team', team)}
            />
          </div>
        </form>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 px-5 py-4 border-t flex-shrink-0"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="project-edit-form"
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-60"
            style={{ backgroundColor: BRAND_COLORS.primary }}
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectEditModal;
